import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import {
  ChannelType,
  EventType,
  ImageSourceType,
  StageSlide,
} from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { eventSliderLogoWidth } from '@/constants/tiles'
import useStore from '@/store/useStore'
import { getBadges } from '@/utils/eventUtils'
import { findNearestByImgWidth } from '@/utils/utils'
import { replaceVideoUrlParams } from '@/utils/videoParamsUtils'

export const getStageSlideMeta = (
  el?: StageSlide,
  isRedeemablePPVEvent?: Nullable<boolean>,
  isFullscreen = false,
  isRegisteredUser = false,
) => {
  const slideData = el
  const slideDataType = slideData?.type
  const isShade = slideData?.shade ?? true

  let backgroundMediaData: string | ChannelType | undefined =
    getBackgroundImageData(slideData)
  let backgroundMediaDataId: string | undefined
  let backgroundMediaFormat = 'image'
  let logoUrl
  let stageTileTitle
  let stageTileDescription
  let isBlockedStage
  let stageTileBadges
  let onEnded
  let isButtonHidden
  let secondaryButtonVariant: null | 'partnerPage' = null
  let tokens: null | {
    amount: number
    walletName: string
  } = null

  const stageTileButtonText = slideData?.buttonText || `Open ${slideDataType}`

  switch (slideDataType) {
    case 'Page': {
      stageTileTitle =
        slideData?.title ||
        slideData?.page?.title ||
        slideData?.page?.shortTitle ||
        slideData?.page?.name
      stageTileDescription = slideData?.description
      break
    }
    case 'Channel': {
      const channelEvent =
        slideData?.channel &&
        useStore.getState().common.channelEvents[slideData.channel.id]
      const event = channelEvent?.isFutureChannelEvent ? null : channelEvent
      stageTileTitle =
        slideData?.title || event?.title || slideData?.channel?.name
      stageTileDescription =
        slideData?.description ||
        event?.description?.long ||
        slideData?.channel?.description

      if (isDevMode) {
        stageTileDescription = `${slideData?.channel?.id} \n ${
          stageTileDescription || ''
        }`
      }

      const { isAccessible } = getContentAccessFlags(
        event,
        slideData?.channel,
        isRegisteredUser,
      )

      isBlockedStage = !isAccessible
      backgroundMediaData = slideData?.channel

      const videoDataSources = slideData?.channel?.video?.sources?.[0]
      let videoFormat
      if (videoDataSources && 'format' in videoDataSources) {
        videoFormat = videoDataSources.format
      } else {
        videoFormat = videoDataSources?.type
      }
      backgroundMediaFormat = videoFormat || ''

      stageTileBadges = getBadgesData(
        slideData,
        isRegisteredUser,
        isRedeemablePPVEvent,
      )
      break
    }
    case 'Event': {
      stageTileTitle = slideData?.title || slideData?.event?.name
      stageTileDescription =
        slideData?.description ||
        slideData?.event?.description?.long ||
        slideData?.event?.description?.short

      stageTileBadges = getBadgesData(
        slideData,
        isRegisteredUser,
        isRedeemablePPVEvent,
      )
      break
    }
    case 'Media': {
      stageTileTitle = slideData?.title
      stageTileDescription = slideData?.description
      const videoUrl = slideData?.media?.video?.sources?.[0]?.url
      if (videoUrl) {
        backgroundMediaData = replaceVideoUrlParams({
          url: videoUrl,
          isFullscreen,
        })
        backgroundMediaDataId = slideData?.media?.video?.id
        backgroundMediaFormat =
          slideData?.media?.video?.sources?.[0].format || ''
        onEnded = slideData?.media?.video?.onEnded
        isButtonHidden = !(stageTileTitle && stageTileDescription)
      } else {
        const imageSources = slideData?.media?.image?.sources
        backgroundMediaData = findNearestByImgWidth(imageSources, 1920)?.url
        isButtonHidden = true
      }
      break
    }
    case 'Promotion': {
      stageTileTitle = slideData?.title || slideData?.promotion?.title
      stageTileDescription =
        slideData?.description || slideData?.promotion?.description
      if (slideData?.promotion?.logo?.sources?.[0].url) {
        logoUrl = slideData?.promotion?.logo.sources?.[0].url
      }
      if (slideData?.promotion?.additionalPartnerPageId) {
        secondaryButtonVariant = 'partnerPage'
      }
      if (
        slideData?.promotion?.wallet?.uniqueId &&
        slideData.promotion.wallet.availableCreditCount !== undefined
      ) {
        tokens = {
          amount: slideData.promotion.wallet.availableCreditCount,
          walletName:
            slideData.promotion.wallet?.uniqueId
              .replace(/^promotion-/, '')
              .replace(/-credits$/, '') || '',
        }
      }
      break
    }
    default:
      break
  }

  if (slideData?.image?.sources?.[0]?.url && !isFullscreen) {
    const imageSources = slideData?.image?.sources
    backgroundMediaData = getBackgroundImageUrl(imageSources)
    backgroundMediaFormat = 'image'
  }
  if (slideData?.video?.sources?.[0]?.url && !isFullscreen) {
    backgroundMediaData = replaceVideoUrlParams({
      url: slideData?.video?.sources?.[0]?.url,
      isFullscreen,
    })
    backgroundMediaDataId = slideData?.video?.id
    backgroundMediaFormat = slideData?.video?.sources?.[0].format || ''
    onEnded = slideData?.video?.onEnded
  }

  return {
    backgroundMediaData,
    backgroundMediaDataId,
    backgroundMediaFormat,
    logoUrl,
    stageTileTitle,
    stageTileDescription,
    stageTileButtonText,
    stageTileBadges,
    isBlockedStage,
    activeStageSlide: slideData,
    isShade,
    onEnded,
    isButtonHidden,
    secondaryButtonVariant,
    tokens,
  }
}

const getBackgroundImageUrl = (sources?: ImageSourceType[]) => {
  const { page } = useStore.getState().page

  const pageBackgroundSources = page?.backgroundImage?.sources
  return (
    findNearestByImgWidth(sources, 1920)?.url ||
    findNearestByImgWidth(pageBackgroundSources, 1920)?.url
  )
}

export const getBackgroundImageData = (slideData?: StageSlide) => {
  const slideDataType = slideData?.type

  let backgroundImageData, imageSources

  switch (slideDataType) {
    case 'Page': {
      imageSources = slideData?.page?.backgroundImage?.sources
      break
    }
    case 'Event': {
      imageSources = slideData?.event?.image?.sources
      break
    }
    case 'Promotion': {
      imageSources = slideData?.promotion?.image?.sources
      break
    }
    default:
      break
  }

  if (imageSources) {
    backgroundImageData = getBackgroundImageUrl(imageSources)
  }

  return backgroundImageData
}

// TODO: move to sdk utils
export const getBadgesData = (
  slideData?: StageSlide,
  isRegisteredUser?: boolean,
  isRedeemablePPVEvent?: Nullable<boolean>,
) => {
  const isChannelSlide = slideData?.type === 'Channel'

  let channel: Nullable<ChannelType> | undefined
  let event: Nullable<EventType> | undefined

  if (isChannelSlide) {
    channel = slideData.channel
    const channelEvent =
      channel && useStore.getState().common.channelEvents[channel.id]
    event = channelEvent?.isFutureChannelEvent ? null : channelEvent
  } else {
    event = slideData?.event
  }

  const { isAccessible, isPremium, isPPVEvent } = getContentAccessFlags(
    event,
    channel,
    isRegisteredUser,
  )
  const isWatchable = Boolean(
    isChannelSlide ? channel?.watchable : event?.watchable,
  )

  const {
    isLiveNow,
    isTodayEvent,
    timeLabel,
    isBlockedPremiumChannel,
    isBlockedPPVEvent,
    isBlockedPromotionEvent,
    isSubscribedPremiumChannel,
    isPurchasedPPVEvent,
  } = getBadges(isAccessible, isPPVEvent, isPremium, isWatchable, event)

  const isLockEvent =
    isPPVEvent && !isAccessible && isWatchable && isRedeemablePPVEvent === false

  const channelSources = channel?.image?.sources || event?.channelLogo?.sources

  const channelLogoImg = findNearestByImgWidth(
    channelSources,
    eventSliderLogoWidth,
    {
      prioritizeSvg: true,
    },
  )?.url

  return {
    isWatchable,
    isLiveNow,
    isTodayEvent,
    timeLabel,
    isBlockedPremiumChannel,
    isSubscribedPremiumChannel,
    isBlockedPPVEvent,
    isBlockedPromotionEvent,
    isPurchasedPPVEvent,
    isLockEvent,
    channelLogoImg,
  }
}
